/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Utils from "./Utils";
import Monster from "./Monster";
import Attributes from "./Attributes";
import SkillStats from "./SkillStats";
import Armor from "./Armor";
import Open5e from "./Open5e";

class TetraCube {
  
    static convertLegacyMonsterToCurrent = (legacyMonster: any): Monster => {
      const monster:Monster = new Monster();

      monster.name = legacyMonster.name;
      monster.size = legacyMonster.size;
      monster.type = legacyMonster.type;
      monster.alignment = legacyMonster.alignment;
      monster.armor_desc = legacyMonster.otherArmorDesc; // "armorName":"natural armor","shieldBonus":0,"natArmorBonus":4,"otherArmorDesc":"20 (natural armor)",

      monster.shortName            = legacyMonster.shortName;
      monster.pluralName           = legacyMonster.pluralName;
      monster.doubleColumns        = legacyMonster.doubleColumns;
      monster.hitDiceType          = legacyMonster.hitDieType;
      monster.extraWhitespace      = legacyMonster.extraWhitespace;
      monster.image.src            = legacyMonster.imageSrc            || legacyMonster.image?.src;
      monster.image.positionRight  = legacyMonster.imagePositionRight  || legacyMonster.image?.positionRight;
      monster.image.positionBottom = legacyMonster.imagePositionBottom || legacyMonster.image?.positionBottom;
      monster.image.rotation       = legacyMonster.imageRotation       || legacyMonster.image?.rotation;
      monster.image.width          = legacyMonster.imageWidth          || legacyMonster.image?.width;
      monster.image.horizontalFlip = legacyMonster.imageHorizontalFlip || legacyMonster.image?.horizontalFlip;
      monster.image.clip           = legacyMonster.imageClip           || legacyMonster.image?.clip;
      // These properties don't have places to live:
      // monster.customSpeed          = legacyMonster.customSpeed; //":false,
      // monster.speedDesc            = legacyMonster.speedDesc; //":"10 ft., climb 10 ft.",
      // monster.properties           = legacyMonster.properties; //":[],
      // monster.understandsBut       = legacyMonster.understandsBut; //":"",
      // monster.damage               = legacyMonster.damage; //":[],

      // subrace
      monster.subtype = legacyMonster.tag || monster.subtype;
  
      // Handle hit dice:
      monster.hit_dice = legacyMonster.hit_dice;
      if (!monster.hit_dice) {
        monster.hitDiceNumber = legacyMonster.hitDice || monster.hitDiceNumber;
        monster.hitDiceType = monster.hitDiceType || monster.hitDiceType;
        if (legacyMonster.hpText?.includes('(')) {
          monster.hit_points = parseInt(legacyMonster.hpText?.split('(')[0].trim());
          monster.hit_dice = legacyMonster.hpText?.split('(')[1].split(')')[0]  || monster.hit_dice;
          monster.hitPointsCustom = legacyMonster.hpText;
        } else {
          const constitution: number = monster.attributes.constitution  || parseInt(legacyMonster.conPoints);
          monster.hitDiceBonus = Attributes.getBonus(constitution) * monster.hitDiceNumber;
          monster.usePcHitDice = monster.usePcHitDice ?? false;
          // monster hit dice use dice average (d4=2.5, d6=3.5, d8=4.5, etc) per level, and then round up (d8 per level: 5, 9, 14, 18, 23, 27...)
          let hit_points = Math.max(1, Math.round(monster.hitDiceNumber * (monster.hitDiceType + 1) / 2) + monster.hitDiceBonus);
          if (monster.usePcHitDice) {
              // PC hit dice use max roll at level one, and then round up (d4=3, d6=4, d8=5) at each level (d8 per level: 8, 13, 18, 23, 28, 33...)
              hit_points = monster.hitDiceType + monster.hitDiceBonus + (monster.hitDiceNumber-1) * (monster.hitDiceType/2 + 1);
          }
          let hit_dice = monster.hitDiceNumber + "d" + monster.hitDiceType;
          if (monster.hitDiceBonus > 0) {
            hit_dice += '+' + monster.hitDiceBonus;
          } else if (monster.hitDiceBonus < 0) {
            hit_dice += monster.hitDiceBonus;
          }
          monster.hit_dice = hit_dice;
          monster.hit_points = hit_points;
          monster.hitPointsCustom = `${hit_points} (${hit_dice})`;
        }
      }
      if (legacyMonster.customHP) {
        monster.hitPointsCustom = legacyMonster.hpText;
      }

      // Handle speeds:
      //"speed":"40","burrowSpeed":"0","climbSpeed":"40","flySpeed":"0","hover":false,"swimSpeed":"40",
      if (!legacyMonster.speed || (typeof legacyMonster.speed === "string") || (typeof legacyMonster.speed === "number")) {
        monster.speed.walk = parseInt(legacyMonster.speed) || 0;
      }
      monster.speed.burrow = legacyMonster.speed.burrow ?? (parseInt(legacyMonster.burrowSpeed) || 0);
      monster.speed.climb  = legacyMonster.speed.climb  ?? (parseInt(legacyMonster.climbSpeed) || 0);
      monster.speed.swim   = legacyMonster.speed.swim   ?? (parseInt(legacyMonster.swimSpeed) || 0);
      monster.speed.fly    = legacyMonster.speed.fly    ?? (parseInt(legacyMonster.flySpeed) || 0);
      monster.speed.hover  = legacyMonster.speed.hover  ?? (legacyMonster.hover || false);
  
      // Handle attributes:
      monster.attributes.strength     = legacyMonster.strength      ?? parseInt(legacyMonster.strPoints);
      monster.attributes.dexterity    = legacyMonster.dexterity     ?? parseInt(legacyMonster.dexPoints);
      monster.attributes.constitution = legacyMonster.constitution  ?? parseInt(legacyMonster.conPoints);
      monster.attributes.intelligence = legacyMonster.intelligence  ?? parseInt(legacyMonster.intPoints);
      monster.attributes.wisdom       = legacyMonster.wisdom        ?? parseInt(legacyMonster.wisPoints);
      monster.attributes.charisma     = legacyMonster.charisma      ?? parseInt(legacyMonster.chaPoints);
      
      // proficiency bonus:
      monster.challenge_rating = '' + (legacyMonster.cr || 0);
      monster.cr = Utils.parseCrString(legacyMonster.cr);
      monster.proficiencyBonus = legacyMonster.customProf || legacyMonster.proficiencyBonus || Utils.getProfBonusByCR(legacyMonster.cr);
      monster.xp = Utils.getXpByCR(legacyMonster.cr);
      
      // Armor class and description
      monster.armor_desc = legacyMonster.armorName;
      if (monster.armor_desc.toLowerCase() === 'none') {
        monster.armor_desc = '';
      }
      monster.armor_class = Armor.getAcFromDexAndArmorDesc(monster.attributes.dexterity, legacyMonster.armorName);
      if (monster.armor_desc.toLowerCase() === 'other') {
        const armorParts = legacyMonster.otherArmorDesc.split('(');
        if (armorParts.length === 2) {
          monster.armor_class = parseInt(armorParts[0].trim());
          monster.armor_desc = armorParts[1].split(')')[0].trim();
        } else {
          monster.armor_desc = legacyMonster.otherArmorDesc;
        }
      }
      if (legacyMonster.armorName.toLowerCase() === 'natural armor') {
        monster.armor_class += legacyMonster.natArmorBonus;
      }
      if (legacyMonster.shieldBonus) {
        monster.armor_desc = [monster.armor_desc, 'Shield'].join(', ');
        monster.armor_class += legacyMonster.shieldBonus;
      }

      // saving throws:
      if (!monster.saving_throws && legacyMonster.sthrows) {
        const sarray = [];
        for (const thrw of legacyMonster.sthrows) {
          const attrShort = thrw.name;
          const attrLong: string = Attributes.getAttrName(attrShort);
          const attrLevel: number = parseInt(legacyMonster[attrLong]);
          const saveLevel: number = Attributes.getBonus(attrLevel) + parseInt(legacyMonster.proficiencyBonus);
          sarray.push(thrw.name + (saveLevel > 0 ? ' +' : ' ') + saveLevel);
        }
        monster.saving_throws = sarray.join(', ');
      }

      // Languages:
      monster.languages = TetraCube.parseLanguages(legacyMonster);
  
      // Condition Immunities:
      if (typeof legacyMonster.condition_immunities !== 'string' ) {
        const condImmuns = [];
        for (const cond of legacyMonster.conditions || []) {
          condImmuns.push(cond.name);
        }
        monster.condition_immunities = condImmuns.join(', ');
      }
  
      // Damage Immunities/resistances/vulnerabilities:
      const immunities = [];
      const resistant = [];
      const vulnerable = [];
      for (const damage of legacyMonster.damagetypes || []) {
        if (damage.type === 'i') immunities.push(damage.name);
        if (damage.type === 'r') resistant.push(damage.name);
        if (damage.type === 'v') vulnerable.push(damage.name);
      }
      for (const damage of legacyMonster.specialdamage || []) {
        if (damage.type === 'i') immunities.push(damage.name);
        if (damage.type === 'r') resistant.push(damage.name);
        if (damage.type === 'v') vulnerable.push(damage.name);
      }
      monster.damage_vulnerabilities = vulnerable.join(', ');
      monster.damage_resistances = resistant.join(', ');
      monster.damage_immunities = immunities.join(', ');

      // Ability Blocks
      monster.actions           = legacyMonster.actions;
      monster.reactions         = legacyMonster.reactions;
      monster.traits            = legacyMonster.abilities;
      monster.bonus_actions     = legacyMonster.bonusActions;
      monster.legendary_desc    = (legacyMonster.isLegendary ? legacyMonster.legendariesDescription : '') || legacyMonster.legendariesDescription;
      monster.legendary_actions = legacyMonster.isLegendary  ? legacyMonster.legendaries            : [];
      monster.mythic_desc       = legacyMonster.isMythic     ? legacyMonster.mythicDescription      : '';
      monster.mythic_actions    = legacyMonster.isMythic     ? legacyMonster.mythics                : [];
      monster.regional_desc     = legacyMonster.isRegional   ? legacyMonster.regionalDescription    : '';
      monster.regional_desc_end = legacyMonster.isRegional   ? legacyMonster.regionalDescriptionEnd : '';
      monster.regional_actions  = legacyMonster.isRegional   ? legacyMonster.regionals              : [];
      monster.lair_desc         = legacyMonster.isLair       ? legacyMonster.lairDescription        : '';
      monster.lair_desc_end     = legacyMonster.isLair       ? legacyMonster.lairDescriptionEnd     : '';
      monster.lair_actions      = legacyMonster.isLair       ? legacyMonster.lairs                  : [];
      monster.inventory         = legacyMonster.hasInventory ? legacyMonster.inventory              : [];
    
      // Skills
      monster.skillSet = SkillStats.importLegacySkills(legacyMonster.skills, monster.attributes, monster.proficiencyBonus);

      monster.splitPoint = legacyMonster.separationPoint;
  
      // Senses:
      const perceptionSkillLevel = monster.skillSet.skills['Perception']?.adjLevel || Attributes.getBonus(monster.attributes.wisdom);
      const senses: string[] = [];
      if (parseInt(legacyMonster.blindsight))  senses.push('blindsight '  + legacyMonster.blindsight + ' ft.' + (legacyMonster.blind ? ' (blind beyond this radius)' : ''));
      if (parseInt(legacyMonster.darkvision))  senses.push('darkvision '  + legacyMonster.darkvision + ' ft.');
      if (parseInt(legacyMonster.tremorsense)) senses.push('tremorsense ' + legacyMonster.tremorsense + ' ft.');
      if (parseInt(legacyMonster.truesight))   senses.push('truesight '   + legacyMonster.truesight + ' ft.');
      const passivePerception = 10 + (perceptionSkillLevel || 0);
      senses.push('Passive Perception ' + passivePerception);
      monster.senses = legacyMonster.senses || senses.join(', ');

      // convert any 'description' into 'desc', and ensure all objects are Ability objects:
      monster.inventory        = Open5e.prepAbilityArray(monster.inventory);
      monster.mythic_actions   = Open5e.prepAbilityArray(monster.mythic_actions);
      monster.regional_actions = Open5e.prepAbilityArray(monster.regional_actions);
      monster.lair_actions     = Open5e.prepAbilityArray(monster.lair_actions);
      
      return monster;
  }

  static parseLanguages(legacyMonster: any) {
    // Languages:
    let languages = '';
    if (typeof legacyMonster.languages !== 'string' ) {
      const langs = [];
      for (const lang of legacyMonster.languages || []) {
        langs.push((lang.speaks ? '' : 'understands ') + lang.name);
      }
      languages = langs.join(', ');
    }
    
    if (legacyMonster.understandsBut) {
      languages += ' but ' + legacyMonster.understandsBut;
    }

    if (legacyMonster.telepathy && legacyMonster.telepathy > 0) {
      languages += ', Telepathy ' + legacyMonster.telepathy + '\'';
    }
    return languages;
  }
}
export default TetraCube;